import { Box, Typography } from '@mui/material';
import CustomTab from 'components/shared/CustomTab/CustomTab';
import SectionLoader from 'components/shared/SectionLoader';
import { useAlertStatus } from 'customHook/useAlertStatus';
import { formatDealAmount } from 'helper/short-currency';
import { apiDealTypes, PIPELINE_MAP } from 'model/deals.model';
import { MouseEvent, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import DealCard from '../DealCard';
import { filtersToQueryParams } from '../DealsFilters/DealsFilters.utils';
import { DealsSettings } from '../DealsSettings';
import { MultiPipelineKanbanProps } from './types';
import { calculateColumnEarnings } from './utils';

const MultiPipelineKanban = ({
  filterState,
  tabValue,
  searchTerm = '',
  needsAttention = false,
}: MultiPipelineKanbanProps) => {
  const [deals, setDeals] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [dealCardElement, setDealCardElement] = useState<SVGSVGElement | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<any>(null);

  const [pipelineTabValue, setPipelineTabValue] = useState(() => {
    return Number(sessionStorage.getItem('activePipelineTab')) || 0;
  });
  const pipelines = deals?.data ? Object.keys(deals.data) : [];
  const selectedPipeline = pipelines[pipelineTabValue] || pipelines[0];

  const pipelineData = deals?.data?.[selectedPipeline];
  const stages = pipelineData?.stages ? Object.keys(pipelineData.stages) : [];

  const getDeals = useCallback(async () => {
    try {
      setLoading(true);

      const queryParams = filtersToQueryParams(filterState, tabValue, searchTerm, needsAttention);

      const response = await DealsService.getMultiPipelineKanbanView(queryParams);
      setDeals(response);
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  }, [filterState, needsAttention, searchTerm, tabValue]);

  useEffect(() => {
    getDeals();
  }, [filterState, tabValue, searchTerm, needsAttention, getDeals]);

  const handleOpenSettings = (event: MouseEvent<SVGSVGElement>, deal: any) => {
    event.stopPropagation();
    setSelectedDeal(deal);
    setDealCardElement(event.currentTarget);
  };

  const { startOperation, completeOperation } = useAlertStatus();

  const handleStageChange = () => {
    getDeals();
  };

  function handleOnDrag(e: React.DragEvent, name: string, sourceStage: string) {
    e.dataTransfer?.setData('name', name);
    e.dataTransfer?.setData('sourceStage', sourceStage);
  }

  function handleOnDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  async function handleOnDrop(e: React.DragEvent, targetStage: any) {
    const name = e.dataTransfer.getData('name');
    const sourceStage = e.dataTransfer.getData('sourceStage');

    if (sourceStage === targetStage) return;

    const prevDeals = pipelineData.stages;
    if (!prevDeals) return;

    const stageDeals = prevDeals[sourceStage].data;

    const dealToMove = stageDeals.find((d: any) => d.name === name);
    if (!dealToMove) return;

    const dealId = dealToMove.dealId;

    if (!dealId) {
      completeOperation('update', 'Deal ID not found', true);
      return;
    }

    setDeals((prev: any) => {
      if (!prev) return prev;

      const prevSourceStageDeals = prev.data[selectedPipeline].stages[sourceStage].data;
      const prevTargetStageDeals = prev.data[selectedPipeline].stages[targetStage].data;

      const newSourceList = prevSourceStageDeals.filter((d: any) => d.name !== name);
      const newTargetList = [prevTargetStageDeals, { ...dealToMove, bucket: targetStage }];

      return {
        ...prev,
        [sourceStage]: newSourceList,
        [targetStage]: newTargetList,
      };
    });

    try {
      startOperation('update', 'Updating deal stage...');

      const payload = { stageName: targetStage };

      await DealsService.updateDeal(dealId, payload);
      await getDeals();

      completeOperation('update', 'Deal stage updated successfully');
    } catch (error: any) {
      console.error('Error updating deal:', error);
      setDeals(prevDeals);
      completeOperation(
        'update',
        error?.response?.data?.message || 'Failed to update deal stage',
        true,
      );
    }
  }

  if (loading) return <SectionLoader />;

  if (!deals && !selectedPipeline) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
      >
        <Typography>No deals found</Typography>
      </Box>
    );
  }

  const tabs = pipelines.map((pipeline, index) => {
    const pipelineName = PIPELINE_MAP[pipeline as apiDealTypes];
    const count = deals.data[pipeline].pipelineCount;

    return {
      label: `${pipelineName} (${count})`,
      id: index + 1,
      isHidden: tabValue !== index,
      Component: <></>,
    };
  });

  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    setPipelineTabValue(newValue);
    sessionStorage.setItem('activePipelineTab', newValue.toString());
  };

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <CustomTab tabs={tabs} handleChange={handleTabChange} value={pipelineTabValue} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          overflow: 'scroll',
          flex: 1,
          gap: '16px',
        }}
      >
        {stages.map((stage) => {
          const { count, data } = pipelineData.stages[stage];
          const earnings = formatDealAmount(calculateColumnEarnings(data));

          return (
            <Box
              key={stage}
              sx={{
                minWidth: '300px',
                width: '100%',
                borderRadius: '14px 14px 0 0',
                padding: '8px',
                backgroundColor: zeplinColor.Neutral2,
              }}
              onDragOver={handleOnDragOver}
              onDrop={(e) => handleOnDrop(e, stage)}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px 8px 0px',
                  marginBottom: '8px',
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Typography
                    variant='heading3'
                    sx={{
                      color: zeplinColor.BasicBlack,
                    }}
                  >
                    {stage} ({count})
                  </Typography>
                </Box>
                <Typography variant='responsiveHeading4' sx={{ color: zeplinColor.Success }}>
                  {earnings}
                </Typography>
              </Box>
              <Box
                id={`kanban-column-${stage}`}
                sx={{
                  height: 'calc(100vh - 470px)',
                  overflowY: 'auto',
                }}
              >
                {data.length === 0 && !loading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      padding: '24px',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        color: zeplinColor.Neutral8,
                        textAlign: 'center',
                      }}
                    >
                      No deals in this stage
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                    }}
                  >
                    {data.map((deal: any) => (
                      <DealCard
                        key={deal.dealId}
                        data={deal}
                        onDrag={(e) => handleOnDrag(e, deal.name, stage)}
                        onSettingsClick={handleOpenSettings}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}

        <DealsSettings
          anchorElement={dealCardElement}
          onClose={() => {
            setDealCardElement(null);
            setSelectedDeal(null);
          }}
          startOperation={startOperation}
          completeOperation={completeOperation}
          deal={selectedDeal}
          onDealDeleted={getDeals}
          onStageChange={handleStageChange}
          stages={stages}
        />
      </Box>
    </>
  );
};

export default MultiPipelineKanban;
